import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useCallback, useContext } from "react";
import { useFragment, useMutation } from "react-relay";
import { InternIntegrationsCardCheckMetaMutation } from "./__generated__/InternIntegrationsCardCheckMetaMutation.graphql";
import { InternIntegrationsCard_brand$key } from "./__generated__/InternIntegrationsCard_brand.graphql";
import SnackbarContext from "../../../contexts/SnackbarContext";
import { InternPanel } from "../components";

const brandFragment = graphql`
  fragment InternIntegrationsCard_brand on BrandType {
    id
    facebookConfig {
      id
      needsReauth
    }
  }
`;

const checkMetaMutation = graphql`
  mutation InternIntegrationsCardCheckMetaMutation($brandId: ID!) {
    checkBrandMetaConnection(brandId: $brandId) {
      facebookConfig {
        id
        needsReauth
      }
      ...InternIntegrationsCard_brand
    }
  }
`;

type Props = {
  brand: InternIntegrationsCard_brand$key;
};

const InternIntegrationsCard = ({ brand: brandKey }: Props) => {
  const brand = useFragment(brandFragment, brandKey);
  const snackbarContext = useContext(SnackbarContext);
  const [checkMetaConnection, isCheckInFlight] =
    useMutation<InternIntegrationsCardCheckMetaMutation>(checkMetaMutation);

  const isMetaConnected = brand.facebookConfig?.id != null;
  const needsMetaReauth = brand.facebookConfig?.needsReauth ?? false;

  const handleCheckMetaConnection = useCallback(() => {
    checkMetaConnection({
      variables: {
        brandId: brand.id,
      },
      onCompleted: (response) => {
        const facebookConfig = response.checkBrandMetaConnection.facebookConfig;
        if (facebookConfig == null) {
          snackbarContext?.openSnackbar(
            "Meta is not connected for this brand.",
            "warning"
          );
        } else if (facebookConfig.needsReauth) {
          snackbarContext?.openSnackbar(
            "Meta re-authentication is required.",
            "warning"
          );
        } else {
          snackbarContext?.openSnackbar("Meta connection is valid.", "success");
        }
      },
      onError: (error) => {
        snackbarContext?.openSnackbar(
          `Meta check failed: ${error.message}`,
          "error"
        );
      },
    });
  }, [brand.id, checkMetaConnection, snackbarContext]);

  return (
    <InternPanel
      title="Integrations"
      subtitle="Third-party connection health"
      icon={<HubOutlinedIcon />}
      collapsible={false}
      actions={
        isMetaConnected ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={
              isCheckInFlight ? <CircularProgress size={14} /> : <SyncIcon />
            }
            onClick={handleCheckMetaConnection}
            disabled={isCheckInFlight}
          >
            {isCheckInFlight ? "Checking..." : "Check now"}
          </Button>
        ) : undefined
      }
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label={isMetaConnected ? "Meta Connected" : "Meta Not Connected"}
            color={isMetaConnected ? "success" : "default"}
            size="small"
            variant={isMetaConnected ? "filled" : "outlined"}
          />
          {isMetaConnected && (
            <Chip
              label={needsMetaReauth ? "Re-auth Required" : "Healthy"}
              color={needsMetaReauth ? "warning" : "success"}
              size="small"
              variant="outlined"
            />
          )}
        </Stack>

        {isMetaConnected ? (
          needsMetaReauth ? (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon fontSize="inherit" />}
              sx={{
                bgcolor: "var(--intern-bg-secondary)",
                color: "var(--intern-text-primary)",
                border: "1px solid rgba(245, 158, 11, 0.35)",
                py: 0.5,
                "& .MuiAlert-icon": {
                  color: "#f59e0b",
                },
                "& .MuiAlert-message": { fontSize: "0.8125rem" },
              }}
            >
              Meta is connected, but re-authentication is required before story
              syncing can resume.
            </Alert>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleOutlineIcon sx={{ color: "#10b981", fontSize: 16 }} />
              <Typography
                variant="body2"
                sx={{ color: "var(--intern-text-tertiary)" }}
              >
                Meta is connected and currently not marked for
                re-authentication.
              </Typography>
            </Stack>
          )
        ) : (
          <Typography
            variant="body2"
            sx={{ color: "var(--intern-text-tertiary)" }}
          >
            This brand does not currently have a Meta connection configured.
          </Typography>
        )}
      </Stack>
    </InternPanel>
  );
};

export default InternIntegrationsCard;
